// NOKE 活動折扣 — where the counter's running promotions are defined.
//
// Admin-only (backend: requireAdminRole on every write). 門市人員 never see this
// page; the POS applies whatever is set here automatically and shows them the
// resulting per-line price, so the floor cannot mis-key a 折數.
//
// The 折數 field is written the way it is spoken in Chinese: 95 means 95折, i.e.
// the customer pays 95%. Storing "pay percent" rather than "discount percent"
// removes the inversion bug that would otherwise sit between this form and the
// pricing engine.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// The API returns validation failures as {error:{message}} (the express error
// handler in server.js) but middleware rejections as a bare {message}. Read
// both, or the specific 折數/門檻 guidance never reaches the person typing it.
const apiMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error?.message || err?.response?.data?.message || fallback;

interface Campaign {
  id: number;
  name: string;
  kind: 'percent' | 'conditional' | 'gift';
  scope: 'all' | 'metanoia' | 'mdoc';
  pay_percent: number | null;
  require_scope: 'all' | 'metanoia' | 'mdoc' | null;
  min_subtotal: number | null;
  gift_label: string | null;
  priority: number;
  starts_on: string | null;
  ends_on: string | null;
  is_active: boolean;
  note: string | null;
  created_by?: string | null;
  live_today?: boolean;
}

type Draft = Omit<Campaign, 'id' | 'live_today' | 'created_by'> & { id?: number };

const KIND_LABEL: Record<Campaign['kind'], string> = {
  percent: '全面折扣',
  conditional: '加價購 (需搭配)',
  gift: '滿額贈',
};

const SCOPE_LABEL: Record<string, string> = {
  all: '全部商品',
  metanoia: 'Metanoia 服飾',
  mdoc: 'MdoC',
};

const emptyDraft = (): Draft => ({
  name: '',
  kind: 'percent',
  scope: 'all',
  pay_percent: 95,
  require_scope: null,
  min_subtotal: null,
  gift_label: null,
  priority: 0,
  starts_on: null,
  ends_on: null,
  is_active: true,
  note: null,
});

// The opening set Magnus already agreed with the mall — prefills the form so
// they can be reviewed and saved one by one rather than retyped.
const PRESETS: { label: string; draft: () => Draft }[] = [
  {
    label: '服飾全面 95 折',
    draft: () => ({ ...emptyDraft(), name: '服飾全面95折', kind: 'percent', scope: 'metanoia', pay_percent: 95 }),
  },
  {
    label: 'MdoC 全面 8 折',
    draft: () => ({ ...emptyDraft(), name: 'MdoC 全面8折', kind: 'percent', scope: 'mdoc', pay_percent: 80 }),
  },
  {
    label: '買服飾 → MdoC 7 折',
    draft: () => ({
      ...emptyDraft(),
      name: '買服飾 MdoC 7折加價購',
      kind: 'conditional',
      scope: 'mdoc',
      require_scope: 'metanoia',
      pay_percent: 70,
      priority: 10,
    }),
  },
  {
    label: '滿 2,000 贈滾珠香水',
    draft: () => ({
      ...emptyDraft(),
      name: '滿2000贈滾珠香水',
      kind: 'gift',
      scope: 'all',
      pay_percent: null,
      min_subtotal: 2000,
      gift_label: '滾珠香水',
    }),
  },
];

const statusOf = (c: Campaign, today: string) => {
  if (!c.is_active) return { label: '停用', color: 'default' as const };
  if (c.starts_on && today < c.starts_on) return { label: '未開始', color: 'info' as const };
  if (c.ends_on && today > c.ends_on) return { label: '已結束', color: 'default' as const };
  return { label: '進行中', color: 'success' as const };
};

const NokeCampaignsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [today, setToday] = useState('');
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/noke/campaigns');
      setCampaigns(res.data.campaigns || []);
      setToday(res.data.today || '');
    } catch (err: any) {
      enqueueSnackbar(apiMessage(err, '載入活動失敗'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      if (draft.id) {
        await api.put(`/api/noke/campaigns/${draft.id}`, draft);
        enqueueSnackbar('活動已更新', { variant: 'success' });
      } else {
        await api.post('/api/noke/campaigns', draft);
        enqueueSnackbar('活動已建立', { variant: 'success' });
      }
      setDraft(null);
      load();
    } catch (err: any) {
      enqueueSnackbar(apiMessage(err, '儲存失敗'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: Campaign) => {
    if (!window.confirm(`確定刪除「${c.name}」？\n已完成的銷售不受影響（各筆銷售保留自己的活動紀錄）。`)) return;
    try {
      await api.delete(`/api/noke/campaigns/${c.id}`);
      enqueueSnackbar('活動已刪除', { variant: 'success' });
      load();
    } catch (err: any) {
      enqueueSnackbar(apiMessage(err, '刪除失敗'), { variant: 'error' });
    }
  };

  const toggleActive = async (c: Campaign) => {
    try {
      await api.put(`/api/noke/campaigns/${c.id}`, { ...c, is_active: !c.is_active });
      load();
    } catch (err: any) {
      enqueueSnackbar(apiMessage(err, '更新失敗'), { variant: 'error' });
    }
  };

  const describe = (c: Campaign) => {
    if (c.kind === 'gift') return `滿 NT$ ${c.min_subtotal?.toLocaleString()} 贈 ${c.gift_label}`;
    const base = `${c.pay_percent} 折`;
    if (c.kind === 'conditional') return `${base}（需同時購買 ${SCOPE_LABEL[c.require_scope || 'all']}）`;
    return base;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          NOKE 活動折扣
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDraft(emptyDraft())}>
          新增活動
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        這裡設定的活動會<strong>自動套用</strong>在 NOKE 收銀上，並依「每件商品取最優惠的一項」計算（活動不疊加）。
        折扣會記在<strong>每一件商品</strong>上，所以品牌分帳（Metanoia / MdoC）永遠等於各品牌實收金額 —
        這是店員要鍵進商場 POS 的數字。
      </Alert>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : campaigns.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            尚未設定任何活動。收銀台目前不會有任何自動折扣。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            開幕活動可以直接套用範本：
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {PRESETS.map((p) => (
              <Chip key={p.label} label={p.label} onClick={() => setDraft(p.draft())} variant="outlined" />
            ))}
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>活動名稱</TableCell>
                <TableCell>類型</TableCell>
                <TableCell>適用</TableCell>
                <TableCell>內容</TableCell>
                <TableCell>期間</TableCell>
                <TableCell align="center">狀態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => {
                const st = statusOf(c, today);
                return (
                  <TableRow key={c.id} hover sx={{ opacity: c.is_active ? 1 : 0.55 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                      {c.note && (
                        <Typography variant="caption" color="text.secondary">
                          {c.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{KIND_LABEL[c.kind]}</TableCell>
                    <TableCell>{SCOPE_LABEL[c.scope]}</TableCell>
                    <TableCell>{describe(c)}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {c.starts_on || '不限'} ~ {c.ends_on || '不限'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" color={st.color} label={st.label} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={c.is_active ? '停用' : '啟用'}>
                        <Switch size="small" checked={c.is_active} onChange={() => toggleActive(c)} />
                      </Tooltip>
                      <IconButton size="small" onClick={() => setDraft({ ...c })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => remove(c)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {campaigns.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            快速新增：
          </Typography>
          {PRESETS.map((p) => (
            <Chip key={p.label} size="small" label={p.label} onClick={() => setDraft(p.draft())} variant="outlined" />
          ))}
        </Box>
      )}

      {/* ---- create / edit dialog ---- */}
      <Dialog open={!!draft} onClose={() => setDraft(null)} fullWidth maxWidth="sm">
        <DialogTitle>{draft?.id ? '編輯活動' : '新增活動'}</DialogTitle>
        <DialogContent>
          {draft && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="活動名稱"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                helperText="會顯示在收銀台上，店員與客人都看得到"
                fullWidth
                autoFocus
              />

              <TextField
                select
                label="類型"
                value={draft.kind}
                onChange={(e) => {
                  const kind = e.target.value as Campaign['kind'];
                  setDraft({
                    ...draft,
                    kind,
                    pay_percent: kind === 'gift' ? null : draft.pay_percent || 95,
                    require_scope: kind === 'conditional' ? draft.require_scope || 'metanoia' : null,
                    min_subtotal: kind === 'gift' ? draft.min_subtotal || 2000 : null,
                    gift_label: kind === 'gift' ? draft.gift_label : null,
                  });
                }}
                fullWidth
              >
                <MenuItem value="percent">全面折扣（指定品牌打折）</MenuItem>
                <MenuItem value="conditional">加價購（買 A 才有的 B 折扣）</MenuItem>
                <MenuItem value="gift">滿額贈（滿多少送贈品）</MenuItem>
              </TextField>

              <TextField
                select
                label={draft.kind === 'gift' ? '門檻計算範圍' : '折扣適用商品'}
                value={draft.scope}
                onChange={(e) => setDraft({ ...draft, scope: e.target.value as Campaign['scope'] })}
                fullWidth
              >
                <MenuItem value="all">全部商品</MenuItem>
                <MenuItem value="metanoia">Metanoia 服飾</MenuItem>
                <MenuItem value="mdoc">MdoC</MenuItem>
              </TextField>

              {draft.kind === 'conditional' && (
                <TextField
                  select
                  label="需同時購買"
                  value={draft.require_scope || 'metanoia'}
                  onChange={(e) => setDraft({ ...draft, require_scope: e.target.value as Campaign['scope'] })}
                  helperText="籃子裡要有這類商品，折扣才會生效"
                  fullWidth
                >
                  <MenuItem value="all">任何商品</MenuItem>
                  <MenuItem value="metanoia">Metanoia 服飾</MenuItem>
                  <MenuItem value="mdoc">MdoC</MenuItem>
                </TextField>
              )}

              {draft.kind !== 'gift' && (
                <TextField
                  label="折數"
                  type="number"
                  value={draft.pay_percent ?? ''}
                  onChange={(e) => setDraft({ ...draft, pay_percent: parseInt(e.target.value, 10) || null })}
                  inputProps={{ min: 1, max: 99 }}
                  helperText="填 95 = 95 折（客人付 95%）；填 80 = 8 折"
                  fullWidth
                />
              )}

              {draft.kind === 'gift' && (
                <>
                  <TextField
                    label="門檻金額 (NT$)"
                    type="number"
                    value={draft.min_subtotal ?? ''}
                    onChange={(e) => setDraft({ ...draft, min_subtotal: parseInt(e.target.value, 10) || null })}
                    inputProps={{ min: 1 }}
                    helperText="以折扣後金額計算"
                    fullWidth
                  />
                  <TextField
                    label="贈品名稱"
                    value={draft.gift_label ?? ''}
                    onChange={(e) => setDraft({ ...draft, gift_label: e.target.value })}
                    helperText="收銀台會提醒店員；贈品仍需掃描並標記為贈品才會扣庫存"
                    fullWidth
                  />
                </>
              )}

              <Divider />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="開始日期"
                  type="date"
                  value={draft.starts_on || ''}
                  onChange={(e) => setDraft({ ...draft, starts_on: e.target.value || null })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="結束日期"
                  type="date"
                  value={draft.ends_on || ''}
                  onChange={(e) => setDraft({ ...draft, ends_on: e.target.value || null })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                留空表示不限。日期以台北時間計算，含頭含尾。
              </Typography>

              <TextField
                label="備註 (選填)"
                value={draft.note || ''}
                onChange={(e) => setDraft({ ...draft, note: e.target.value || null })}
                multiline
                minRows={2}
                fullWidth
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.is_active}
                    onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                  />
                }
                label="啟用"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>取消</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !draft?.name?.trim()}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            儲存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NokeCampaignsPage;
